import { ApiError } from '@core/api/apiError';
import type { RedemptionCancelResult } from '@core/api/rewards';

// Исход «Отменить» на экране кода награды (K-012, решение владельца №2: баллы списаны при запросе,
// клиент может отменить резерв — баллы возвращаются сразу). Чистые функции: экран только показывает.

export interface CancelOutcome {
  /** Закрыть экран кода (резерва больше нет либо держать его открытым бессмысленно). */
  close: boolean;
  tone: 'success' | 'error';
  text: string;
  /** Бэкенд ещё без маршрута отмены — кнопку «Отменить» больше не показываем. */
  unsupported?: boolean;
}

/** «1 балл», «2 балла», «5 баллов». */
export function pointsWord(n: number): string {
  const mod10 = Math.abs(n) % 10;
  const mod100 = Math.abs(n) % 100;
  if (mod10 === 1 && mod100 !== 11) return 'балл';
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'балла';
  return 'баллов';
}

/**
 * Бэкенд без маршрута: общий 404 роутера (NOT_FOUND) или 405. Свой «не найден» новый бэкенд отдаёт как
 * REDEMPTION_NOT_FOUND — это не «маршрута нет».
 */
export function isRouteMissing(error: unknown): boolean {
  if (!(error instanceof ApiError)) return false;
  return (
    (error.status === 404 && error.code === 'NOT_FOUND') ||
    error.status === 405 ||
    error.code === 'METHOD_NOT_ALLOWED'
  );
}

export function cancelOutcomeFromResult(r: RedemptionCancelResult): CancelOutcome {
  const pts = `${r.refunded} ${pointsWord(r.refunded)}`;
  if (r.status === 'EXPIRED') {
    return { close: true, tone: 'success', text: `Код уже истёк — вернули на счёт: ${pts}.` };
  }
  return { close: true, tone: 'success', text: `Обмен отменён. Вернули на счёт: ${pts}.` };
}

/**
 * Бэкенд без отмены: честно говорим, что баллы вернутся по истечении кода (а не «Баллы возвращены»).
 * timeLeft — остаток таймера кода («07:42»).
 */
export function unsupportedCancelOutcome(timeLeft?: string): CancelOutcome {
  const when = timeLeft && timeLeft !== '00:00' ? `через ${timeLeft}` : 'скоро';
  return {
    close: true,
    tone: 'error',
    unsupported: true,
    text: `Отмена пока недоступна. Не показывайте код кассиру — ${when} он сгорит, и баллы вернутся автоматически.`,
  };
}

export function cancelOutcomeFromError(error: unknown, timeLeft?: string): CancelOutcome {
  if (isRouteMissing(error)) {
    return unsupportedCancelOutcome(timeLeft);
  }
  const code = error instanceof ApiError ? error.code : '';
  switch (code) {
    case 'POS_ORDER_IN_PROGRESS':
      return {
        close: false,
        tone: 'error',
        text: 'Заказ уже передан на кассу — отменить обмен можно только у кассира.',
      };
    case 'INVALID_STATUS':
      return { close: true, tone: 'error', text: 'Награда уже выдана — отменить обмен нельзя.' };
    case 'REDEMPTION_NOT_FOUND':
      return { close: true, tone: 'error', text: 'Обмен не найден — баланс обновлён.' };
    default: {
      const msg = error instanceof Error && error.message ? error.message : 'Что-то пошло не так';
      return { close: false, tone: 'error', text: `Не удалось отменить: ${msg} Попробуйте ещё раз.` };
    }
  }
}
